import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Button, Dialog } from '@laura/design-system';
import { createApiClient, type ApiClient } from './api.js';
import { onClinicSwitch } from './clinic-switch.js';
import type { AuthBackend } from './auth.js';
import { readClaims, type Claims } from './claims.js';
import { clearActivity, useIdleSignOut } from './idle.js';
import { StepUpDialog } from './screens.js';

export type SessionStatus = 'loading' | 'signedOut' | 'signedIn';

export interface Session {
  status: SessionStatus;
  claims?: Claims;
  email: string;
  backend: AuthBackend;
  api: ApiClient;
  /** True after an idle sign-out (ADR 238), until the next sign-in. */
  idleSignedOut: boolean;
  /**
   * Plan 257 EF-08 follow-up: signed out because this sign-in did not use the second
   * factor the account has (the API held every call); the sign-in page says why.
   */
  mfaSignedOut: boolean;
  /**
   * ADR 242: the clinic this page loaded with, and true once another tab switched to
   * another clinic (this page then reloads; nothing more is sent with the new token).
   */
  bootTenant?: string;
  switchedElsewhere: boolean;
  /** Reads the token again (after sign-in, MFA set-up or a role change). */
  refresh: (force?: boolean) => Promise<void>;
  rememberEmail: (email: string) => void;
  signOut: (everywhere?: boolean) => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

const EMAIL_KEY = 'laura.email';

function storedEmail(): string {
  try {
    return globalThis.sessionStorage?.getItem(EMAIL_KEY) ?? '';
  } catch {
    return '';
  }
}

export function SessionProvider({
  backend,
  apiUrl,
  idleMinutes,
  children,
}: {
  backend: AuthBackend;
  apiUrl: string;
  /** ADR 238 (Q-05): sign out after this many minutes without activity; off when unset. */
  idleMinutes?: number;
  children: ReactNode;
}) {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [claims, setClaims] = useState<Claims>();
  const [email, setEmail] = useState(storedEmail);
  const [stepUpOpen, setStepUpOpen] = useState(false);
  const [idleSignedOut, setIdleSignedOut] = useState(false);
  const [mfaSignedOut, setMfaSignedOut] = useState(false);
  const stepUpResolve = useRef<((ok: boolean) => void) | null>(null);
  // ADR 242 (plan 242 §6): the first clinic this page signed in to. A later token for
  // another clinic means another tab switched: requests stop and the page reloads.
  const bootTenant = useRef<string | undefined>(undefined);
  const [switchedElsewhere, setSwitchedElsewhere] = useState(false);
  const unauthorized = useRef<() => void>(() => undefined);
  const held = useRef<() => void>(() => undefined);

  const refresh = useCallback(
    async (force = false) => {
      try {
        const token = await backend.accessToken(force);
        if (!token) {
          setClaims(undefined);
          setStatus('signedOut');
          return;
        }
        const read = readClaims(token);
        bootTenant.current ??= read.tenantId;
        setClaims(read);
        setStatus('signedIn');
        setIdleSignedOut(false);
        setMfaSignedOut(false);
      } catch {
        setClaims(undefined);
        setStatus('signedOut');
      }
    },
    [backend],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const rememberEmail = useCallback((value: string) => {
    setEmail(value);
    try {
      globalThis.sessionStorage?.setItem(EMAIL_KEY, value);
    } catch {
      // storage blocked: the email is asked again at step-up
    }
  }, []);

  const api = useMemo(
    () =>
      createApiClient({
        baseUrl: apiUrl,
        token: (force) => backend.accessToken(force),
        stepUp: () =>
          new Promise<boolean>((resolve) => {
            stepUpResolve.current = resolve;
            setStepUpOpen(true);
          }),
        expectedTenant: () => bootTenant.current,
        onTenantMismatch: () => setSwitchedElsewhere(true),
        onUnauthorized: () => unauthorized.current(),
        onAccessHeld: () => held.current(),
      }),
    [apiUrl, backend],
  );

  // ADR 242: another tab switched clinic. This page shows the notice and reloads.
  useEffect(
    () =>
      onClinicSwitch((tenantId) => {
        if (bootTenant.current !== undefined && tenantId !== bootTenant.current)
          setSwitchedElsewhere(true);
      }),
    [],
  );

  const signOut = useCallback(
    async (everywhere = false) => {
      await backend.signOut(everywhere);
      setClaims(undefined);
      setStatus('signedOut');
    },
    [backend],
  );

  // ADR 242 (plan 242 §6): still 401 after a refresh. A personal token (no clinic
  // left, or several to choose from) keeps the person signed in for the picker; a token
  // for another clinic means a switch elsewhere; anything else signs out.
  unauthorized.current = () => {
    void (async () => {
      const token = await backend.accessToken(true).catch(() => undefined);
      const read = token ? readClaims(token) : undefined;
      if (read?.personal) {
        setClaims(read);
        return;
      }
      if (read?.tenantId !== undefined && read.tenantId !== bootTenant.current) {
        setSwitchedElsewhere(true);
        return;
      }
      await signOut().catch(() => undefined);
    })();
  };

  // Plan 257 EF-08 follow-up: the API holds every call but the page still shows the old
  // claims (read before the last token refresh). Read the token again: a factor the
  // account has but this sign-in did not use means signing in again with the code; no
  // factor at all shows the set-up hold; anything else (a clinic not active) is left to
  // the page.
  held.current = () => {
    void (async () => {
      const token = await backend.accessToken(true).catch(() => undefined);
      const read = token ? readClaims(token) : undefined;
      if (!read) return;
      if (read.mfaEnrolled === true && read.mfa !== true) {
        setMfaSignedOut(true);
        await signOut().catch(() => {
          setClaims(undefined);
          setStatus('signedOut');
        });
        return;
      }
      setClaims(read);
    })();
  };

  // A session that ended without a sign-out (an expired refresh token) leaves no
  // stale activity behind for the next sign-in.
  useEffect(() => {
    if (status === 'signedOut') clearActivity();
  }, [status]);

  useIdleSignOut({
    minutes: idleMinutes,
    active: status === 'signedIn',
    onIdle: () => {
      setIdleSignedOut(true);
      // This device only: other devices keep their own idle clocks.
      void signOut().catch(() => {
        setClaims(undefined);
        setStatus('signedOut');
      });
    },
  });

  // A reload at `/` drops every cache, the socket and per-clinic state (plan 242 §6).
  const reload = () => globalThis.location.assign('/');

  // ADR 242: the notice reloads by itself after a few seconds.
  useEffect(() => {
    if (!switchedElsewhere) return;
    const id = setTimeout(reload, 4000);
    return () => clearTimeout(id);
  }, [switchedElsewhere]);

  const finishStepUp = (ok: boolean) => {
    setStepUpOpen(false);
    stepUpResolve.current?.(ok);
    stepUpResolve.current = null;
    void refresh();
  };

  const value: Session = {
    status,
    ...(claims && { claims }),
    email,
    backend,
    api,
    idleSignedOut,
    mfaSignedOut,
    ...(bootTenant.current !== undefined && { bootTenant: bootTenant.current }),
    switchedElsewhere,
    refresh,
    rememberEmail,
    signOut,
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
      <StepUpDialog open={stepUpOpen} backend={backend} email={email} onDone={finishStepUp} />
      <Dialog
        open={switchedElsewhere}
        title="You switched clinic in another tab"
        onClose={reload}
        footer={
          <Button variant="primary" onClick={reload}>
            Open it here
          </Button>
        }
      >
        <p>This tab now opens the clinic you chose there.</p>
      </Dialog>
    </SessionContext.Provider>
  );
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession outside SessionProvider');
  return session;
}
